import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Info } from 'lucide-react';
import { getFullLoad, getSmallLoadSurcharge, getSmallLoadThreshold, MIN_LOAD } from '../utils/loadSplit';
/**
 * Shared small-load warning used across the Hero estimator, the materials
 * hub product cards, the Product detail page — anywhere we surface
 * single-load pricing.
 *
 * Two distinct cases:
 *
 * 1. ORDER MINIMUM bump (quantity < MIN_LOAD): a standalone single-delivery
 *    order below 3 yd gets bumped up to 3 yd. We tell the customer they're
 *    paying for more than they asked for and explain why.
 *
 * 2. SMALL-LOAD SURCHARGE (MIN_LOAD ≤ quantity < threshold): a single load
 *    below the truck's threshold (12 yd light / 10 yd heavy) is billed at
 *    a higher per-yard rate. The info panel explains the delivery-fee vs
 *    per-yard split so customers see this isn't an extra charge — it's
 *    just the math of running a truck below capacity. Requires
 *    pricePerYard to render — without it, falls through to return null.
 *
 * Multi-delivery orders (quantity > full load) get partial-load pricing
 * handled per-line in the cart breakdown, so this component returns null
 * in those cases.
 */
export function SmallLoadBreakdown({
  quantity,
  productName,
  pricePerYard,
  className = ''
}: {
  quantity: number;
  productName: string;
  pricePerYard?: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  if (!quantity || quantity <= 0) return null;
  const fullLoad = getFullLoad(productName);
  if (quantity > fullLoad) return null;
  const threshold = getSmallLoadThreshold(productName);
  // Case 1 — sub-MIN_LOAD bump
  if (quantity < MIN_LOAD) {
    return <div className={className} data-id="element-1995">
        <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-start gap-1.5 text-left text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={open} data-id="element-1996">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-1997" />
          <span data-id="element-1998">
            You asked for {quantity} yds — billed for {MIN_LOAD} yds. Why?
          </span>
        </button>
        <AnimatePresence data-id="element-1999">
          {open && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-2000">
              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-1.5" data-id="element-2001">
                <p data-id="element-2002">
                  <span className="font-bold" data-id="element-2003">
                    {MIN_LOAD}-yard order minimum.
                  </span>{' '}
                  Trucks have fixed dispatch costs, so any single delivery is
                  billed at {MIN_LOAD} yds even if you only need a couple.
                </p>
                <p data-id="element-2004">
                  Round up to fill the truck and you'll get more material at no
                  extra cost. Need more than one truck? Partial loads in a
                  multi-delivery order are billed at their actual yardage.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  // Case 2 — small-load surcharge (only when we know the base price)
  if (pricePerYard && pricePerYard > 0 && quantity >= MIN_LOAD && quantity < threshold) {
    const surcharge = getSmallLoadSurcharge(quantity, productName);
    const effectivePerYard = Math.ceil(pricePerYard * surcharge);
    return <div className={className} data-id="element-2005">
        <button type="button" onClick={() => setOpen(v => !v)} className="inline-flex items-start gap-1.5 text-left text-xs text-amber-500 dark:text-amber-400 font-medium hover:underline" aria-expanded={open} data-id="element-2006">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" data-id="element-2007" />
          <span data-id="element-2008">
            Includes small load upcharge (${effectivePerYard}/yd vs $
            {pricePerYard}/yd at {threshold}+ yards). Why?
          </span>
        </button>
        <AnimatePresence data-id="element-2009">
          {open && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-2010">
              <div className="mt-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-stone-700 dark:text-white/85 leading-relaxed space-y-2" data-id="element-2011">
                <p data-id="element-2012">
                  <span className="font-bold" data-id="element-2013">
                    Why both a delivery fee and a small-load rate?
                  </span>{' '}
                  They cover two different things.
                </p>
                <ul className="space-y-1.5 list-none pl-0" data-id="element-2014">
                  <li data-id="element-2015">
                    <span className="font-semibold text-stone-800 dark:text-white" data-id="element-2016">
                      Delivery fee
                    </span>{' '}
                    = the round-trip to your address (distance, fuel, driver
                    time). Same whether the truck is full or half-empty.
                  </li>
                  <li data-id="element-2017">
                    <span className="font-semibold text-stone-800 dark:text-white" data-id="element-2018">
                      Per-yard rate
                    </span>{' '}
                    = the material plus loading it at the yard (loader operator,
                    scale, ticketing). That loading work takes about the same
                    time whether we scoop 3 yds or {fullLoad} yds.
                  </li>
                </ul>
                <p data-id="element-2019">
                  When you order less than a full load, that fixed loading cost
                  is split across fewer yards — so the per-yard price goes up.
                  It's not an extra fee, just the math of running a truck below
                  capacity.
                </p>
                <p className="text-stone-600 dark:text-white/70" data-id="element-2020">
                  Tip: rounding up to {threshold}+ yards drops you to{' '}
                  <span className="font-bold text-stone-800 dark:text-white" data-id="element-2021">
                    ${pricePerYard}/yd
                  </span>{' '}
                  — usually more material for not much more money.
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>;
  }
  return null;
}